import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import RailHeader from '../home/RailHeader';

/**
 * CommunityPicksHistory — a grid of previously funded Community Fund picks.
 *
 * Props:
 *   pastWinners — from useSlicePot().pastWinners
 *     Each entry: {
 *       id, starts_at, ends_at, pot_awarded, closed_at,
 *       winner: {
 *         id, title, campaign_image_urls, total_slices_raised, goal_slices, status,
 *         publisher: { id, company_name, brand_logo_url, is_verified },
 *         work: { id, title, slug, poster_url, backdrop_url }
 *       }
 *     }
 */
const CommunityPicksHistory = ({ pastWinners }) => {
  if (!pastWinners || pastWinners.length === 0) return null;

  /* Filter out any periods with a missing winner */
  const validPicks = pastWinners.filter((p) => !!p.winner);
  if (validPicks.length === 0) return null;

  return (
    <section>
      <RailHeader title="Community Picks" />
      <p className="text-sm text-muted-foreground -mt-1 mb-4">
        Projects the community funded together through the collective pot.
      </p>

      {/* Grid */}
      <div className="grid gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {validPicks.map((period) => (
          <PastPickCard key={period.id} period={period} />
        ))}
      </div>
    </section>
  );
};

/* ─── Individual past pick card ───────────────────────────────── */
const PastPickCard = ({ period }) => {
  const campaign  = period.winner;
  const publisher = campaign.publisher;
  const work      = campaign.work;

  const image =
    campaign.campaign_image_urls?.[0] || work?.poster_url || null;

  const pct =
    (campaign.goal_slices ?? 0) > 0
      ? Math.min(
          Math.round((campaign.total_slices_raised / campaign.goal_slices) * 100),
          100
        )
      : 0;

  const closedDate = period.closed_at
    ? new Date(period.closed_at).toLocaleDateString(undefined, {
        month: 'short',
        year:  'numeric',
      })
    : null;

  const isWatchable = !!work?.slug;

  return (
    <div className="rounded-lg border border-border/40 overflow-hidden bg-card/20 flex flex-col">
      {/* Image */}
      {image && (
        <div className="aspect-[4/5] bg-muted overflow-hidden">
          <img
            src={image}
            alt={campaign.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-4 flex flex-col gap-3 flex-1">
        {/* Funded-by label */}
        <p className="text-[10px] uppercase tracking-widest text-amber-500 font-medium">
          Funded by the Community Fund
        </p>

        {/* Campaign info */}
        <div>
          <h3 className="font-semibold text-sm leading-snug text-foreground">
            {campaign.title}
          </h3>
          {publisher?.company_name && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {publisher.company_name}
            </p>
          )}
        </div>

        {/* Pot awarded + date */}
        <div className="space-y-0.5">
          <div className="flex items-baseline justify-between text-xs text-muted-foreground tabular-nums">
            <span>Pot awarded</span>
            <span className="text-amber-400 font-medium">
              🍕 {(period.pot_awarded ?? 0).toLocaleString()}
            </span>
          </div>
          {closedDate && (
            <p className="text-[10px] text-muted-foreground">{closedDate}</p>
          )}
        </div>

        {/* Outcome: watchable link OR raised progress */}
        <div className="mt-auto pt-1">
          {isWatchable ? (
            <Link
              to={`/watch/${work.slug}`}
              className="group inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              Now watchable
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          ) : (
            <div className="space-y-1.5">
              <div className="relative h-[2px] bg-border/60 w-full overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-primary"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-[10px] text-muted-foreground">
                {pct}% funded · still in production
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunityPicksHistory;
